import type { ScheduledJobView, ScheduledRunOutcome } from '@numeo/types'

/** Times show in the viewer's own time zone. */
export const formatTime = (iso: string): string =>
    new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })

export function formatDuration(ms: number): string {
    if (ms < 1000) return `${ms} ms`
    const s = ms / 1000
    if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)} s`
    const m = Math.floor(s / 60)
    if (m < 60) return `${m} min ${Math.round(s % 60)} s`
    return `${Math.floor(m / 60)} h ${m % 60} min`
}

/** "in 4 min", "12 min ago". */
export function formatRelative(iso: string, now: number): string {
    const diff = new Date(iso).getTime() - now
    const abs = Math.abs(diff)
    const text =
        abs < 60_000 ? `${Math.max(1, Math.round(abs / 1000))} s` : abs < 3_600_000 ? `${Math.round(abs / 60_000)} min` : `${(abs / 3_600_000).toFixed(1)} h`
    return diff >= 0 ? `in ${text}` : `${text} ago`
}

export const OUTCOME_LABEL: Record<ScheduledRunOutcome, string> = {
    succeeded: 'Succeeded',
    failed: 'Failed',
    timed_out: 'Timed out',
    skipped: 'Skipped',
}

export const OUTCOME_CLASS: Record<ScheduledRunOutcome, string> = {
    succeeded: 'text-emerald-700 dark:text-emerald-400',
    failed: 'text-rose-700 dark:text-rose-400',
    timed_out: 'text-amber-700 dark:text-amber-400',
    skipped: 'text-slate-500 dark:text-slate-400',
}

/** A job whose last run did not succeed, or that failed several times in a row. */
export const isFailing = (job: ScheduledJobView): boolean =>
    job.consecutiveFailures > 0 || job.lastRun?.outcome === 'failed' || job.lastRun?.outcome === 'timed_out'

export type JobFilter = 'all' | 'active' | 'failing' | 'not_started'

export function matchesFilter(job: ScheduledJobView, filter: JobFilter, search: string): boolean {
    const q = search.trim().toLowerCase()
    if (q && !`${job.name} ${job.purpose} ${job.calls}`.toLowerCase().includes(q)) return false
    if (filter === 'active') return job.status === 'waiting' || job.status === 'running'
    if (filter === 'failing') return isFailing(job)
    if (filter === 'not_started') return job.status === 'not_started'
    return true
}
